import { useMutation, useQuery } from "@apollo/client";
import { CREATE_COMMENT, GET_COMMENT } from "../../../services/graphql/queriesMutations";
import { CommentType, UseCommentsOptions, UseCommentsReturn } from "../types";

/**
 * Custom hook for managing comments with nested replies
 * Provides loading, error, refetch, and create functionality
 */
export function useComments({ editId, gistId }: UseCommentsOptions): UseCommentsReturn {
    // Fetch comments with nested replies
    const { data, loading, error, refetch } = useQuery(GET_COMMENT, {
        variables: { gistId: gistId || null, editId: editId || null },
        skip: !gistId && !editId,
    });

    // Create comment mutation
    const [createCommentMutation, { loading: isCreating }] = useMutation(CREATE_COMMENT);

    // Filter to get only root-level comments (parentId === null)
    const rootComments: CommentType[] = (data?.comments || []).filter(
        (c: CommentType) => c.parentId === null
    );

    // Create a new comment or reply
    const createComment = async (content: string, parentId: number | null = null) => {
        if (!content.trim()) return;

        try {
            await createCommentMutation({
                variables: {
                    newComment: {
                        comment: content,
                        parentId,
                        gistId,
                        editId,
                    },
                },
            });

            // Refetch to get updated comments with new reply
            await refetch();
        } catch (err) {
            console.error("Failed to create comment:", err);
            throw err;
        }
    };

    return {
        comments: rootComments,
        loading,
        error,
        refetch,
        createComment,
        isCreating,
    };
}

export default useComments;
